/* DIREVULF — header behaviour: sticky scroll, and voice search.

   STICKY
   The announcement bar is a separate section above the header, so it is left
   to scroll away on its own. That is what makes the rule "the announcement
   comes back only at the very top" true by construction rather than by a
   timer: once it has scrolled out of view the only way to see it again is to
   return to the top of the page. This script only decides what the sticky
   header itself shows.

     scroll down   -> phone: the search strip goes, the black bar stays;
                      tablet/desktop: is-compact, nav folds
     scroll up     -> strip / nav return
     at the top    -> every state class removed, full header restored

   VOICE
   The browser's own SpeechRecognition and nothing else. There is no assistant
   here, no request to any server of ours, no speech synthesis, and no audio is
   recorded, stored or uploaded by the theme: the browser returns a transcript,
   the transcript goes in the search field, and the existing search form does
   the searching.

   HOW PERMISSION IS ASKED FOR, AND WHY IT IS ASKED FOR THIS WAY
   getUserMedia is called first, synchronously inside the click, so the browser
   raises its own permission prompt at a moment it will honour, and so the
   answer comes back as a specific error rather than as SpeechRecognition's
   single opaque "not-allowed". The audio track is stopped the instant it
   arrives — the theme only ever wanted the permission, never the stream.

   Nothing latches. An earlier refusal is not remembered, because a shopper who
   has since granted the microphone in their browser must be able to simply tap
   the button again and have it work.

   If the browser cannot do speech recognition the microphone is removed
   outright — a control that cannot work is worse than no control — and typing
   is completely unaffected.

   ONE IMPLEMENTATION, EVERY MICROPHONE
   The sticky-header logic below needs the header. The voice logic does not: it
   binds every [data-dv-mic] on the page, so the search box on the results page
   behaves exactly like the one in the header without a second engine existing
   anywhere. Each microphone talks to its own form and reports into the nearest
   [data-dv-mic-status]. */
(function () {
  'use strict';

  var header = document.querySelector('[data-dv-header]');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var strings = {};
  try {
    var node = document.getElementById('dv-header-strings');
    if (node) strings = JSON.parse(node.textContent);
  } catch (e) { strings = {}; }

  /* ------------------------------------------------------------- sticky */
  /* The distance the page must travel before the header is allowed to
     compact, so a one-pixel wobble or a focus jump never collapses it. */
  if (header) stickyHeader(header);

  /* THE HEADER'S ONE AND ONLY SCROLL STATE.

     One passive scroll listener, one rAF-gated read, one function that writes
     classes — and it writes only when the state actually changes.

       at the top (<= 4px)      full header, nothing added
       scrolling down           phone (<= 640px): the search strip goes and the
                                black bar stays stuck to the top, always;
                                tablet and desktop: the nav row folds away
                                (`is-compact`), the bar stays
       scrolling up             everything comes back

     WHY IT USED TO FAIL TO FOLLOW THE SHOPPER
     Direction was decided by comparing each frame with the frame before —
     `y > lastY + 10`. That is a SPEED test, not a distance test: a finger
     moving slower than 10px per frame (a normal, gentle scroll) never passed
     it, however far it went, so the header simply stayed where it was, and
     only a fast flick changed it. It now measures from the point where the
     direction last turned (`anchorY`): 12px of travel in one direction flips
     the state (10px on a phone), whatever the speed, and a reversal smaller
     than that is ignored, so a wobble or momentum scroll's last pixels cannot
     flicker it.

     THE PHONE: WHAT THIS SCRIPT DOES, AND WHAT IT LEAVES TO THE STYLESHEET
     The strip is not hidden by this script. By default the sticky section may
     ride up by the strip's height before it sticks (storefront-3.css), so on
     the way down the strip leaves with the page itself, from the first pixel,
     with no distance to wait for and nothing here to run. All this script
     adds is the way back: 10px of upward travel sets `is-search-shown` on the
     section, which makes the whole header stick — strip and all — and 10px
     of downward travel takes the class off again. The bar is sticky inside
     the header and never moves in either state.

     WHY IT CANNOT BLINK
     The first version collapsed the strip's height, which moved the page and
     fed a scroll-anchoring correction back in as a fake scroll the other way.
     The second slid the whole header off with a transform, bar included.
     `is-search-shown` changes one sticky offset and nothing else: no height,
     no margin, no transform on the page, so the document's height and scrollY
     are exactly what they were and no scroll event comes back from the
     change. The settle lock below is kept for tablet and desktop only, where
     folding the nav row does change height.

     THE MOBILE ADDRESS BAR, AND THE BOUNCE AT THE ENDS
     On iOS the toolbar collapses as you scroll down and returns as you scroll
     up — at exactly the moment the direction changes. A viewport resize does
     not move the document (scrollY stays put), so it resets nothing. The
     rubber band at the top reads as 0 and the one at the bottom is clamped to
     the last real position on a phone, so springing back from either end is
     not counted as the shopper scrolling the other way.

     The strip is never taken away while it is in use — see stripHeld(). On
     tablet and desktop the nav row is never folded while something in the
     header has focus. */
  function stickyHeader(header) {
    var section = header.closest('.shopify-section') || header.parentElement;
    var root = document.documentElement;
    var bar = header.querySelector('.header-main');
    var strip = header.querySelector('[data-dv-strip]');
    var stripForm = strip ? strip.querySelector('form') : null;
    var phone = window.matchMedia ? window.matchMedia('(max-width: 640px)') : { matches: false };
    var HIDE_AFTER = 90;   /* tablet/desktop: never below the header's own height + the announcement */
    var TRAVEL = 12;       /* tablet/desktop: px of travel in one direction before the state flips */
    var TRAVEL_PHONE = 10; /* phone: the same test, with no minimum depth */
    var AT_TOP = 4;
    var SETTLE = 280;      /* desktop nav fold: 240ms transition + a couple of frames */

    var anchorY = Math.max(window.pageYOffset || 0, 0);
    var lastY = anchorY;
    var dir = 0;           /* 1 = moving down, -1 = moving up, 0 = unknown */
    /* The state: true while the shopper is heading down. A phone starts there,
       because from the top of the page down is the only way to go, and the
       strip then leaves with the first pixel instead of after a threshold. */
    var down = phone.matches;
    var ticking = false;
    var lockUntil = 0;
    var stuck = null, compact = null, shown = null, away = null;

    /* `is-search-away` — the strip can be neither tapped nor focused. It is
       set only once the header's lower edge has come up to the field's top
       edge, which is the moment the field is entirely cut off, so the class
       never changes anything that is on screen. Measured, not assumed: no
       height or offset is written down here to go stale. */
    function setAway(on) {
      if (on === away) return;
      if (section) section.classList.toggle('is-search-away', on);
      away = on;
    }

    function readAway() {
      if (!phone.matches || !stripForm || shown) { setAway(false); return; }
      setAway(header.getBoundingClientRect().bottom <= stripForm.getBoundingClientRect().top + 0.5);
    }

    /* What sticks or lands under the header on a phone (storefront-3.css)
       needs two numbers no stylesheet can reach from outside this section: the
       bar's height and the whole header's. They are measured here, published
       on <html>, and taken away again above 640px. Neither changes with the
       scroll, so this runs once and again only when the breakpoint is crossed.
       The Create Your Own studio takes the strip out of the header after this
       has run. The full height stays the page's own there, which is the taller
       of the two, so whatever lands under it is still clear of the header. */
    function publish() {
      if (!phone.matches) {
        root.style.removeProperty('--dv-header-bar');
        root.style.removeProperty('--dv-header-full');
        return;
      }
      root.style.setProperty('--dv-header-bar', (bar ? bar.offsetHeight : header.offsetHeight) + 'px');
      root.style.setProperty('--dv-header-full', header.offsetHeight + 'px');
    }

    function write(isStuck, isDown) {
      var small = phone.matches;
      var wantCompact = isDown && !small;
      var wantShown = small && isStuck && !isDown;
      var heightChanges = wantCompact !== compact;
      if (isStuck !== stuck) { header.classList.toggle('is-stuck', isStuck); stuck = isStuck; }
      if (wantCompact !== compact) { header.classList.toggle('is-compact', wantCompact); compact = wantCompact; }
      if (wantShown !== shown) {
        if (wantShown) setAway(false);   /* in reach again before the shutter opens on it */
        if (section) section.classList.toggle('is-search-shown', wantShown);
        shown = wantShown;
      }
      if (heightChanges && !small) lockUntil = Date.now() + SETTLE;
      readAway();
    }

    function focusInside() {
      var a = document.activeElement;
      return !!(a && a !== document.body && header.contains(a));
    }

    /* A keyboard is on screen: the visual viewport is much shorter than the
       window while a field is being typed in. */
    function keyboardUp() {
      var vv = window.visualViewport;
      var a = document.activeElement;
      if (!vv || !a || a === document.body) return false;
      if (!a.isContentEditable && !/^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return false;
      return window.innerHeight - vv.height > 120;
    }

    /* The strip is in use, and is not to be taken from under the shopper's
       hands: its field or its microphone has focus (which is also when its
       keyboard is up), the microphone is listening, or the suggestions list is
       open under it. All of it is read from the page as it stands — nothing in
       the voice code or in search.js is asked to cooperate. */
    function stripHeld() {
      if (!strip || !stripForm) return false;
      var a = document.activeElement;
      if (a && a !== document.body && strip.contains(a)) return true;
      if (strip.querySelector('.search-mic.is-listening')) return true;
      var input = stripForm.querySelector('input[type="search"], input[name="q"]');
      var listId = input ? input.getAttribute('aria-controls') : null;
      var list = listId ? document.getElementById(listId) : null;
      var panel = list && list.closest ? list.closest('[data-dv-suggest]') : null;
      return !!(panel && !panel.hidden);
    }

    function apply() {
      ticking = false;
      var small = phone.matches;
      var raw = window.pageYOffset || document.documentElement.scrollTop || 0;
      var y = raw > 0 ? raw : 0;

      if (small) {
        /* The rubber band at the bottom of the page is not a scroll. */
        var max = (document.documentElement.scrollHeight || 0) - window.innerHeight;
        if (max > 0 && y > max) y = max;
      }

      if (y <= AT_TOP) {
        down = small; dir = 0; anchorY = y; lastY = y;
        write(false, false);
        return;
      }

      /* Tablet/desktop only: while the nav row is folding, the scroll position
         is the browser correcting for it, not the shopper. Follow it silently. */
      if (Date.now() < lockUntil) {
        anchorY = y; lastY = y;
        return;
      }

      var step = y - lastY;
      if (step !== 0) {
        var d = step > 0 ? 1 : -1;
        if (d !== dir) { dir = d; anchorY = lastY; }   /* direction turned here */
      }
      lastY = y;

      var next = down;
      if (small) {
        if (dir === 1 && y - anchorY >= TRAVEL_PHONE) next = true;
        else if (dir === -1 && anchorY - y >= TRAVEL_PHONE) next = false;
        if (stripHeld()) next = false;                          /* in use: it stays */
        else if (next && !down && keyboardUp()) next = false;   /* a keyboard is up: it is not taken away */
      } else {
        if (dir === 1 && y - anchorY >= TRAVEL && y > HIDE_AFTER) next = true;
        else if (dir === -1 && anchorY - y >= TRAVEL) next = false;
        if (next && focusInside()) next = false;
      }
      down = next;

      write(true, down);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    /* Crossing the 640px line (rotation, a resized window) moves the state to
       the representation that breakpoint uses. */
    function onBreakpoint() {
      compact = null; shown = null;
      if (stuck !== true) down = phone.matches;
      publish();
      write(stuck === true, down);
    }
    if (phone.addEventListener) phone.addEventListener('change', onBreakpoint);
    else if (phone.addListener) phone.addListener(onBreakpoint);

    /* Focus moving into the header (tapping the search field) brings it back.
       On a phone that means the strip's own controls: focus going back to the
       menu button when the drawer closes is no reason to open the strip. */
    header.addEventListener('focusin', function () {
      if (!down) return;
      if (phone.matches && !stripHeld()) return;
      down = false;
      write(stuck === true, false);
    });

    /* The strip finishing its way out is not a scroll, so nothing above would
       notice it: the end of the section's own `top` transition is when the
       field has just gone out of sight, and so out of reach. */
    if (section) {
      section.addEventListener('transitionend', function (event) {
        if (event.target === section && event.propertyName === 'top') readAway();
      });
    }

    /* An open overlay locks the body, which fires no scroll events; leaving the
       header in whatever state it was in is correct, so nothing to do there. */
    window.addEventListener('scroll', onScroll, { passive: true });
    publish();
    apply();
  }

  /* -------------------------------------------------------------- voice */
  /* Every microphone on the page, wherever it is: the header's, and the one on
     the search results page. */
  var mics = $$('[data-dv-mic]');
  if (!mics.length) return;

  var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    /* Unsupported: take the control out of the DOM and say nothing. Text
       search is untouched, which is the entire fallback. */
    mics.forEach(function (m) { m.remove(); });
    return;
  }

  var clearTimer = null;
  var live = null;

  /* The status line belonging to this microphone's own form or section, so a
     message never appears next to a search box the shopper is not using. */
  function statusFor(mic) {
    var scope = mic.closest('.dv-searchpage, .store-header, form');
    var node = scope ? scope.querySelector('[data-dv-mic-status]') : null;
    if (!node && scope && scope.parentElement) {
      node = scope.parentElement.querySelector('[data-dv-mic-status]');
    }
    return node || $('[data-dv-mic-status]');
  }

  function say(message, tone, sticky) {
    if (!live) return;
    window.clearTimeout(clearTimer);
    live.textContent = message || '';
    live.hidden = !message;
    live.classList.toggle('is-error', tone === 'error');
    /* A message about a finished attempt clears itself; "listening" stays up
       for as long as the microphone is actually open. */
    if (message && !sticky) {
      clearTimer = window.setTimeout(function () {
        live.textContent = '';
        live.hidden = true;
        live.classList.remove('is-error');
      }, 5000);
    }
  }

  var recognition = null;
  var activeMic = null;
  var activeInput = null;
  var previousValue = '';
  var gotFinal = false;
  var stopTimer = null;

  /* Longer than any single search phrase, short enough that a microphone left
     open by accident closes itself. */
  var MAX_LISTEN = 12000;

  function inputFor(mic) {
    var form = mic.closest('form');
    return form ? form.querySelector('input[type="search"], input[name="q"]') : null;
  }

  function paint(mic, listening) {
    if (!mic) return;
    mic.setAttribute('aria-pressed', listening ? 'true' : 'false');
    mic.setAttribute(
      'aria-label',
      mic.getAttribute(listening ? 'data-label-stop' : 'data-label-start') || ''
    );
    mic.classList.toggle('is-listening', listening);
  }

  /* Puts everything back the way it was. `restore` undoes the interim text in
     the field, which is right for a cancellation and wrong for a search. */
  function release(message, tone, restore) {
    window.clearTimeout(stopTimer);
    paint(activeMic, false);
    if (restore && activeInput) activeInput.value = previousValue;
    activeMic = null;
    activeInput = null;
    gotFinal = false;
    if (message !== null) say(message, tone);
  }

  function abort(message) {
    if (recognition) {
      try { recognition.abort(); } catch (e) { /* already finished */ }
    }
    release(message, null, true);
  }

  function submit(input) {
    var form = input.closest('form');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  function startRecognition(mic, input) {
    recognition = new SR();
    recognition.lang = document.documentElement.lang || 'en-IN';
    /* Interim results are what make the control visibly alive: the words
       appear in the field as they are recognised, so a shopper can see it is
       working rather than wonder whether the tap registered. */
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = function () {
      activeMic = mic;
      activeInput = input;
      previousValue = input.value;
      gotFinal = false;
      paint(mic, true);
      say(strings.listening || '', null, true);
      window.clearTimeout(stopTimer);
      stopTimer = window.setTimeout(function () {
        /* stop(), not abort(): whatever has been heard so far is still worth
           searching for. */
        if (recognition) { try { recognition.stop(); } catch (e) { /* done */ } }
      }, MAX_LISTEN);
    };

    recognition.onresult = function (event) {
      var interim = '';
      var final = '';
      for (var i = event.resultIndex; i < event.results.length; i++) {
        var text = '';
        try { text = event.results[i][0].transcript || ''; } catch (e) { text = ''; }
        if (event.results[i].isFinal) final += text;
        else interim += text;
      }

      if (final.trim()) {
        gotFinal = true;
        var phrase = final.trim();
        input.value = phrase;
        say((strings.heard || '').replace('%TEXT%', phrase));
        /* The transcript goes into the field the shopper can see and edit,
           and then the ordinary search form runs. There is no separate
           search path, and nothing is sent anywhere else. */
        release(null, null, false);
        if (recognition) { try { recognition.stop(); } catch (e) { /* done */ } }
        submit(input);
        return;
      }

      if (interim) {
        input.value = interim;
        say(strings.processing || strings.listening || '', null, true);
      }
    };

    recognition.onerror = function (event) {
      var code = event && event.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        release(strings.denied || '', 'error', true);
        return;
      }
      if (code === 'aborted') { release(null, null, false); return; }
      if (code === 'no-speech') {
        release(strings.nospeech || strings.error || '', 'error', true);
        return;
      }
      release(strings.error || '', 'error', true);
    };

    recognition.onend = function () {
      if (!activeMic) return;
      /* Ended with nothing final: the shopper stopped it, or it heard only
         noise. Either way the field goes back to what they had typed. */
      if (!gotFinal) release(strings.cancelled || '', null, true);
      else release(null, null, false);
    };

    try { recognition.start(); }
    catch (e) { release(strings.error || '', 'error', true); }
  }

  function listen(mic) {
    live = statusFor(mic);
    /* A second tap on a listening microphone stops it and searches for
       whatever it heard. Escape is the cancel that keeps the typed text. */
    if (activeMic === mic) {
      if (recognition) { try { recognition.stop(); } catch (e) { /* done */ } }
      return;
    }
    if (activeMic) abort(null);

    var input = inputFor(mic);
    if (!input) return;

    /* No mediaDevices at all (an old browser, or an insecure context): let
       SpeechRecognition ask in its own way and report whatever it gets. */
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      startRecognition(mic, input);
      return;
    }

    /* Called here, synchronously, so it still counts as happening inside the
       shopper's click — some browsers refuse a permission prompt otherwise. */
    var asking;
    try { asking = navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) { startRecognition(mic, input); return; }

    paint(mic, true);
    say(strings.listening || '', null, true);

    asking.then(
      function (stream) {
        /* The permission was the point; the audio was not. */
        try { stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) { /* nothing to stop */ }
        paint(mic, false);
        startRecognition(mic, input);
      },
      function (error) {
        paint(mic, false);
        var name = error && error.name;
        if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
          say(strings.denied || '', 'error');
          return;
        }
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          say(strings.error || '', 'error');
          return;
        }
        /* Something else went wrong getting the stream; speech recognition
           may still manage on its own, so let it try and speak for itself. */
        startRecognition(mic, input);
      }
    );
  }

  /* A pointer press should not leave a focus ring behind, and a keyboard press
     must. The browser cannot tell the two apart once focus has been taken by
     script, so the press marks itself and the first keystroke unmarks it;
     storefront-14.css hides the ring only while the mark is there. */
  function dropPointerFocus(event) {
    event.currentTarget.removeAttribute('data-dv-pointer-focus');
  }

  mics.forEach(function (mic) {
    mic.hidden = false;
    /* The field reserves room for the microphone only once it is really
       there, so a browser without speech recognition keeps the original
       padding and the layout is untouched. */
    var form = mic.closest('form');
    if (form) form.classList.add('has-mic');

    /* Focus is taken here rather than left to the browser's own default,
       for one reason: preventScroll. The header's microphone is inside a
       sticky bar and is always on screen, but the one on the search results
       page is in the ordinary flow, and focusing a control that is only
       half in view is enough for the browser to scroll the page to it. The
       shopper asked to speak, not to be moved somewhere else.

       preventDefault() on mousedown suppresses only the browser's own
       focusing; the click event still fires, so listen() runs exactly as
       before. */
    mic.addEventListener('mousedown', function (event) {
      event.preventDefault();
      mic.setAttribute('data-dv-pointer-focus', '');
      try { mic.focus({ preventScroll: true }); }
      catch (e) { mic.focus(); }
    });
    mic.addEventListener('keydown', dropPointerFocus);
    mic.addEventListener('blur', dropPointerFocus);

    mic.addEventListener('click', function (event) {
      event.preventDefault();
      listen(mic);
    });
  });

  /* Escape cancels outright and gives the shopper their typed text back. */
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && activeMic) abort(strings.cancelled || '');
  });
  window.addEventListener('pagehide', function () { if (activeMic) abort(null); });
})();